'use client';

import type {
  AutoFixAllowanceView,
  AutoFixPause,
  AutoFixUpgradeLinkSource,
} from '@/lib/auto-fix-allowance/model';
import { ArrowUpRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  buildAutoFixUpgradeHref,
  buildAutoFixUsagePath,
  describeAutoFixPauseCopy,
} from '@/lib/auto-fix-allowance/model';
import { cn } from '@/lib/utils';

// Long enough that sweeping the pointer across the row opens nothing.
const OPEN_DELAY_MS = 150;
// Time to cross the gap between the trigger and the card without it closing.
const CLOSE_DELAY_MS = 150;
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/** Refreshes at the reset deadline, splitting waits beyond the browser's timer limit. */
function usePauseReset({ resetAt, reason }: Pick<AutoFixPause, 'resetAt' | 'reason'>) {
  const [, setRevision] = useState(0);
  useEffect(() => {
    // Neither comes back by itself: a plan without auto-fix, and a CI hold, which waits for a retry.
    if (reason === 'not_included' || reason === 'ci_on_hold') {
      return;
    }
    const resetTime = new Date(resetAt).getTime();
    if (!Number.isFinite(resetTime)) {
      return;
    }

    let timer: number | null = null;
    const schedule = () => {
      const remaining = resetTime - Date.now();
      if (remaining <= 0) {
        timer = null;
        setRevision((revision) => revision + 1);
        return;
      }
      timer = window.setTimeout(schedule, Math.min(remaining, MAX_TIMER_DELAY_MS));
    };
    // Also covers a deadline crossed between rendering and running the effect.
    schedule();
    return () => {
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [resetAt, reason]);
}

/** Opens after the pointer rests or on focus, and stays open while the pointer is over the card. */
function useHoverOpen() {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const schedule = useCallback(
    (next: boolean, delay: number) => {
      clear();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setOpen(next);
      }, delay);
    },
    [clear],
  );
  useEffect(() => clear, [clear]);
  return {
    open,
    setOpen: (next: boolean) => {
      clear();
      setOpen(next);
    },
    show: () => schedule(true, OPEN_DELAY_MS),
    hide: () => schedule(false, CLOSE_DELAY_MS),
    keepOpen: clear,
  };
}

const ROW_CLASS = 'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';
// An action reads as a link; a statement does not.
const ACTION_CLASS = 'underline decoration-current/40 underline-offset-2 hover:decoration-current';

/**
 * A pause on one line, case by case (`describeAutoFixPauseCopy`): the next step worded as an action
 * when there is one (upgrade, which opens pricing with the plan picked, or Resume), or what
 * happened when there is none. Hovering or focusing it opens a card that says what happened, with
 * a link to the usage page for the plan's usage, when it resets, and the upgrade again. Resume
 * needs no card.
 */
export function AutoFixPauseStatus({
  pause,
  allowance = null,
  installationId,
  canManageBilling = null,
  source,
  onResume,
  isResuming = false,
  className,
  'data-testid': testId,
}: {
  'pause': AutoFixPause;
  /**
   * The organization's live usage. Once it has room, Resume replaces the plan's upgrade, before
   * the reset.
   */
  'allowance'?: AutoFixAllowanceView | null;
  /** The repository's installation, whose usage page the card's arrow opens. */
  'installationId'?: number | null;
  /**
   * Whether the viewer can change the plan. Someone who cannot gets the same calls to action,
   * leading to the usage page, which asks a workspace admin, instead of pricing.
   */
  'canManageBilling'?: boolean | null;
  /** The surface the links are tagged with. */
  'source': AutoFixUpgradeLinkSource;
  /** Starts a new run once the limit has reset or the organization has room. */
  'onResume': () => void;
  'isResuming'?: boolean;
  'className'?: string;
  'data-testid'?: string;
}) {
  const { open, setOpen, show, hide, keepOpen } = useHoverOpen();
  usePauseReset(pause);
  const now = new Date();
  const copy = describeAutoFixPauseCopy(pause, now, allowance);
  const { row } = copy;
  const usageHref = buildAutoFixUsagePath(pause, installationId);
  const upgradeHref =
    row.kind === 'upgrade'
      ? buildAutoFixUpgradeHref({
          offer: row.offer,
          source,
          canManageBilling,
          usagePath: usageHref,
        })
      : null;
  const opens =
    upgradeHref === usageHref ? 'opens usage in a new tab' : 'opens pricing in a new tab';

  const resumeClass = cn(
    ROW_CLASS,
    ACTION_CLASS,
    'disabled:no-underline disabled:opacity-60',
    className,
  );
  // Resume after a reset needs no card; a retry the PR waits for (a CI hold) says why on hover.
  if (row.kind === 'resume' && !copy.body) {
    return (
      <button
        type="button"
        disabled={isResuming}
        onClick={onResume}
        className={resumeClass}
        data-testid={testId}
      >
        {isResuming ? 'Resuming…' : row.label}
      </button>
    );
  }

  const hover = {
    'aria-expanded': open,
    'onPointerEnter': show,
    'onPointerLeave': hide,
    'onFocus': () => setOpen(true),
    'data-testid': testId,
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {row.kind === 'upgrade' ? (
          <a
            href={upgradeHref ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${row.label} (${opens})`}
            className={cn(ROW_CLASS, ACTION_CLASS, className)}
            {...hover}
          >
            {row.label}
          </a>
        ) : row.kind === 'resume' ? (
          <button
            type="button"
            disabled={isResuming}
            onClick={onResume}
            className={resumeClass}
            {...hover}
          >
            {isResuming ? 'Resuming…' : row.label}
          </button>
        ) : (
          // A statement, focusable so keyboard users reach its card.
          // A tap opens it too, where there is no hover.
          <span
            tabIndex={0}
            className={cn(ROW_CLASS, 'cursor-default', className)}
            onClick={() => setOpen(true)}
            {...hover}
          >
            {row.label}
          </span>
        )}
      </PopoverAnchor>
      {/* In place, not in a portal, so Tab moves from the row into the card's links. */}
      <PopoverContent
        portal={false}
        side="top"
        align="start"
        className="w-68 whitespace-normal border-zinc-800 bg-zinc-900 p-3 text-xs text-zinc-300"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={keepOpen}
        onPointerLeave={hide}
        data-testid="auto-fix-pause-card"
      >
        {/* The usage arrow sits beside the title, never after the reset date. */}
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-zinc-100">{copy.title}</span>
          {usageHref ? (
            <a
              href={usageHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Auto-fix usage (opens in a new tab)"
              title="Auto-fix usage"
              className="mt-px shrink-0 rounded-sm text-zinc-500 outline-none hover:text-zinc-300 focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="auto-fix-pause-card-usage"
            >
              <ArrowUpRight aria-hidden className="size-3.5" />
            </a>
          ) : null}
        </div>
        {copy.body ? <p className="mt-1.5 text-xs text-zinc-300">{copy.body}</p> : null}
        {/* When it resets on the left, the upgrade at the end. */}
        {copy.footnote !== null || row.kind === 'upgrade' ? (
          <div
            className={cn(
              'flex items-center justify-between gap-2',
              row.kind === 'upgrade' ? 'mt-2.5 min-h-6' : 'mt-1.5',
            )}
          >
            <span className="text-zinc-500">{copy.footnote}</span>
            {row.kind === 'upgrade' ? (
              <a
                href={upgradeHref ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${row.offer.secondary} (${opens})`}
                className="rounded-md bg-zinc-100 px-2 py-0.5 font-medium text-zinc-900 outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="auto-fix-pause-card-upgrade"
              >
                {row.offer.secondary}
              </a>
            ) : null}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
